/**
 * R268hh · Feb 2026 — Customer / Vendor header for the Composer.
 *
 * Pulled out of `Composer.jsx` so unrelated composer state (line items,
 * TDS, totals) doesn't re-render this heavy combobox on every keystroke.
 * Wrapped in `React.memo` — the parent must pass stable callbacks.
 *
 * Renders:
 *   • Searchable customer combobox (hydrates from Client Overview)
 *   • Optional GSTIN + Customer-state fields (GST-registered orgs)
 *   • Multi-line billing address
 *   • R268u one-tap "Save new client" nudge
 */
import { memo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";

function CustomerHeader({
  docType,
  customer,
  setCustomer,
  clients,
  states,
  gstinCheck,
  checkGstin,
  gstOn,
  saveNewClient,
  setSaveNewClient,
  isEdit,
}) {
  const label = docType === "vendor_bill" || docType === "debit_note" ? "Vendor" : "Customer";

  const onPick = (v) => {
    // R268t · Selecting a saved client pulls the full Client → Overview
    // payload (legal name, tax ID, multi-line billing address, state)
    // so nothing has to be re-typed on the invoice.
    const cli = clients.find(
      (c) => (c.legal_name || c.display_name || c.name) === v);
    if (!cli) {
      setCustomer((c) => ({ ...c, name: v }));
      return;
    }
    const printName = cli.legal_name || cli.display_name || cli.name || v;
    const isGstin = (cli.tax_id_label || "").toUpperCase() === "GSTIN";
    const gstin = isGstin ? (cli.tax_id_value || "") : "";
    const addr = [
      cli.billing_address_line1,
      cli.billing_address_line2,
      [cli.billing_city, cli.billing_state, cli.billing_postal_code]
        .filter(Boolean).join(", "),
      cli.billing_country,
    ].filter(Boolean).join("\n");
    let stateCode = "";
    if (cli.billing_state) {
      const bs = String(cli.billing_state).toLowerCase().trim();
      const hit = states.find(
        (s) => (s.name || "").toLowerCase() === bs || String(s.code) === bs);
      stateCode = hit?.code || "";
    }
    setCustomer({
      name: printName,
      gstin: gstin.toUpperCase(),
      state_code: stateCode || "",
      billing_address: addr,
    });
    if (gstin) checkGstin(gstin.toUpperCase());
  };

  return (
    <section className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <SearchableSelect
        value={customer.name}
        onChange={onPick}
        onCreate={(text) => setCustomer((c) => ({ ...c, name: text }))}
        allowCreate
        options={clients.map((c) => {
          const label = c.legal_name || c.display_name || c.name || "Unnamed client";
          const isGstin = (c.tax_id_label || "").toUpperCase() === "GSTIN";
          return {
            value: label,
            label,
            hint: [
              isGstin && c.tax_id_value ? `GSTIN ${c.tax_id_value}` : null,
              c.billing_city, c.billing_state,
            ].filter(Boolean).join(" · "),
          };
        })}
        placeholder="Search or add a customer *"
        searchPlaceholder="Search clients by name, GSTIN, city…"
        testid="gst-composer-customer-name"
        emptyLabel="No clients yet — type to add one."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {gstOn && (
          <div>
            <Input
              data-testid="gst-composer-customer-gstin"
              placeholder="Customer GSTIN (optional)"
              className="h-11 font-mono uppercase"
              value={customer.gstin}
              maxLength={15}
              onChange={(e) => checkGstin(e.target.value.toUpperCase())}
            />
            {gstinCheck && (
              <div className="mt-1 text-xs flex items-center gap-1"
                      data-testid="gst-composer-gstin-check">
                {gstinCheck.valid ? (
                  <><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-emerald-700">{gstinCheck.state_name}</span></>
                ) : (
                  <><ShieldAlert className="h-3.5 w-3.5 text-rose-600" />
                    <span className="text-rose-700">{gstinCheck.reason}</span></>
                )}
              </div>
            )}
          </div>
        )}
        {gstOn && (
          <SearchableSelect
            value={customer.state_code || ""}
            onChange={(v) => setCustomer((c) => ({ ...c, state_code: v }))}
            options={states.map((s) => ({
              value: s.code, label: `${s.code} · ${s.name}`,
            }))}
            placeholder="Customer state"
            searchPlaceholder="Search state by name or code…"
            testid="gst-composer-customer-state"
          />
        )}
      </div>
      <Textarea
        data-testid="gst-composer-customer-addr"
        placeholder="Billing address (optional)"
        rows={2}
        value={customer.billing_address}
        onChange={(e) => setCustomer((c) => ({ ...c, billing_address: e.target.value }))}
      />
      {/* R268u · Offer to save unknown customer names to Clients in one tap.
          Only surfaces when the typed name doesn't already match. */}
      {!isEdit && customer.name?.trim() &&
        !clients.some((c) => (c.legal_name || c.display_name || c.name)?.toLowerCase() === customer.name.trim().toLowerCase()) && (
        <label className="flex items-start gap-2 text-xs bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 p-2 rounded cursor-pointer"
                  data-testid="gst-composer-save-new-client-wrap">
          <input
            type="checkbox"
            className="mt-0.5"
            data-testid="gst-composer-save-new-client"
            checked={saveNewClient}
            onChange={(e) => setSaveNewClient(e.target.checked)}
          />
          <div>
            <div className="font-semibold text-emerald-800 dark:text-emerald-200">
              Save “{customer.name}” to Clients
            </div>
            <div className="text-emerald-800/80 dark:text-emerald-200/80">
              We'll create a client with the name, GSTIN, and billing address you entered — so next time it's just one tap.
            </div>
          </div>
        </label>
      )}
    </section>
  );
}

export default memo(CustomerHeader);
